// src/AuthContext.js
import { createContext, useState, useEffect, useContext } from 'react';
import { auth, getRedirectResult } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { requestHostAccess as requestHostAccessCallable } from './Auth/host/hostService';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);

  // Handle redirect result from Google sign-in
  useEffect(() => {
    console.log('Checking for redirect result...');
    console.log('Current auth.currentUser:', auth.currentUser);

    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          console.log('Redirect sign-in successful:', result.user.email);
          console.log('User UID:', result.user.uid);
        } else {
          console.log('No redirect result found');
          console.log('auth.currentUser after check:', auth.currentUser);
        }
      })
      .catch((error) => {
        console.error('Error with redirect sign-in:', error.code, error.message);
      });
  }, []);

  useEffect(() => {
    console.log('Setting up onAuthStateChanged listener...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('onAuthStateChanged fired:', user ? user.email : 'no user');
      if (user) {
        setCurrentUser(user);

        // Host status is a custom auth claim, not a Firestore field
        // (F-11: a Firestore field on the user's own doc would be
        // client-writable).
        try {
          const tokenResult = await user.getIdTokenResult();
          setIsHost(tokenResult.claims.isHost === true);
        } catch (err) {
          console.error('Error fetching host status:', err);
        }

        const metadata = user.metadata;
        const isNewUser = metadata.creationTime === metadata.lastSignInTime;
        const signUpSentKey = `signUpEventSent_${user.uid}`;
        const signUpSent = localStorage.getItem(signUpSentKey);

        if (isNewUser && !signUpSent) {
          let method = 'email';
          if (user.providerData.length > 0) {
            const providerId = user.providerData[0].providerId;
            method = providerId.includes('google') ? 'google' : providerId;
          }

          const gclid = localStorage.getItem('gclid') || '';

          sendSignUpEvent(user.uid, gclid, method);
          localStorage.setItem(signUpSentKey, 'true');
        }
      } else {
        setCurrentUser(null);
        setIsHost(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const sendSignUpEvent = (userId, gclid, method = 'email') => {
    if (window.gtag) {
      window.gtag('event', 'sign_up', {
        method: method,
        gclid: gclid,
        user_id: userId
      });
    } else {
      console.error('gtag is not defined');
    }
  };

  const grantHostAccess = async (accessCode) => {
    if (!currentUser) return false;
    try {
      await requestHostAccessCallable(accessCode);
      // setCustomUserClaims only takes effect on the next token refresh.
      const tokenResult = await currentUser.getIdTokenResult(true);
      setIsHost(tokenResult.claims.isHost === true);
      return true;
    } catch (err) {
      console.error('Error granting host access:', err);
      return false;
    }
  };

  const value = {
    currentUser,
    loading,
    isHost,
    grantHostAccess
   };

  return (
    <AuthContext.Provider value={ value }>
      {children}
    </AuthContext.Provider>
  );
};